import React, { useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import { CABIN_CATEGORIES } from 'constants/cruise'
import { useIntlDateFormatter, convertTZ } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY, INTL_SHORT_WEEKDAY_COMMA_DAY_SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'components/Luxkit/Typography/Caption'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseEvergreenInclusionList from 'components/Cruises/CruiseInclusion/CruiseEvergreenInclusionList'
import Group from 'components/utils/Group'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import Heading from 'components/Luxkit/Typography/Heading'
import Divider from 'components/Luxkit/Divider'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import { useCruiseOfferInclusions } from 'hooks/Cruise/useCruiseOfferInclusions'
import CruiseStandardOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseStandardOnboardCreditLabel'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'

const Tile = styled.div`
  display: block;
  align-items: center;
  text-align: left;
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  border-radius: ${props => props.theme.borderRadius.S};

  ${mediaQueryUp.tablet} {
    display: flex;
    padding: ${rem(16)} ${rem(32)};
  }

  > * {
    flex-grow: 1;
  }
`
const Arrow = styled.div`
  justify-content: center;
  width: 100%;
  display: inline-flex;
  align-items: center;
  position: relative;
`

const ArrowLine = styled.div`
  width: 100%;
  background: ${props => props.theme.palette.neutral.default.one};
  height: 1px;
  float: left;
`

const ArrowPoint = styled.div`
  border: solid ${props => props.theme.palette.neutral.default.one};
  border-width: 0 1px 1px 0;
  display: inline-block;
  padding: 1px 5px 5px 1px;
  transform: rotate(-45deg);
  -webkit-transform: rotate(-45deg);
  position: absolute;
  right: 0;
`

const SeparatorHorizontal = styled.div`
  margin: 0 ${rem(22)};
  border-right: 1px solid ${props => props.theme.palette.neutral.default.six};
  height: 100%;
  min-height: ${rem(150)};
  width: 1%;
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: none;
  flex-grow: 0;
  max-width: ${rem(96)};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(152)};
  }
`

const ArrowContainer = styled.div`
  transform: translateY(50%);
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  width: 100%;
  flex-grow: 0;
  position: relative;

  ${mediaQueryUp.tablet} {
    position: static;
  }
`

const Prices = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding-top: ${rem(16)};
  justify-content: space-between;
  gap: ${rem(16)};
  ${mediaQueryUp.desktop} {
    gap: unset;
    padding-top: unset;
    display: flex;
  }
`

const ContentRow = styled.div`
  flex-direction: row;
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const CabinPrice = styled(ContentRow)`
  margin-top: 0;
  justify-content: start;
  gap: ${rem(3)};

  > :last-child {
    display: flex;
    align-self: center;
  }
`

type SailingDetails = {
  departure: string;
  departureWeekday?: string;
  arrival: string;
  arrivalWeekday?: string;
}

interface Props {
  cruise: App.CruiseOffer;
  departure: App.CruiseDepartureDetails;
  onCabinInfoModal: (cabin: App.CabinCategoryType) => void;
  onHandleDepartureModal: (departure: App.CruiseDepartureDetails) => void;
}

function CruiseDepartures(props: Props) {
  const { cruise, departure, onCabinInfoModal, onHandleDepartureModal } = props
  const [portDetails, setPortDetails] = useState<SailingDetails | null>(null)
  const [cabinFormattedDates, setCabinFormattedDates] = useState<SailingDetails | null>(null)

  const isMobileScreen = useScreenSizeOnly('mobile')
  const intlDateFormatter = useIntlDateFormatter()

  const { standardInclusions, luxPlusInclusions } = useCruiseOfferInclusions({ departure })

  useEffect(() => {
    if (departure.arrivalDate && departure.departureDate) {
      const INTL = isMobileScreen ? INTL_DMY_CASUAL_FORMAT : INTL_SHORT_WEEKDAY_COMMA_DAY_SHORT_MONTH_LONG_YEAR

      // Departure and Arrival dates are related to the port timezone, but the timezone is not provided.
      // To enforce the date to stay the same even in negative timezones (such as Americas), we convert the date to UTC
      const arrivalDate = convertTZ(new Date(departure.arrivalDate), 'UTC')
      const arrivalDateFormatted = intlDateFormatter(arrivalDate, INTL)

      const departureDate = convertTZ(new Date(departure.departureDate), 'UTC')
      const departureDateFormatted = intlDateFormatter(departureDate, INTL)

      setCabinFormattedDates({
        departure: departureDateFormatted,
        departureWeekday: intlDateFormatter(departureDate, INTL_LONG_WEEKDAY),
        arrival: arrivalDateFormatted,
        arrivalWeekday: intlDateFormatter(arrivalDate, INTL_LONG_WEEKDAY),
      })

      setPortDetails({
        departure: isMobileScreen ? cruise.departurePort.split(',')[0] : cruise.departurePort,
        arrival: isMobileScreen ? cruise.returnPort.split(',')[0] : cruise.returnPort,
      })
    }
  }, [cruise, departure, isMobileScreen, intlDateFormatter])

  const cabinPrices: App.LowestPricesByCategory = useMemo(() => {
    return cruise.lowestDeparturePricesByCategory[departure.id]
  }, [cruise.lowestDeparturePricesByCategory, departure.id])

  const promotion = useMemo(() => {
    if (!cabinPrices) return null

    const lowestOverallPriceDetails = Object.keys(cabinPrices).reduce((lowest, category) => {
      if (cabinPrices[category].total < lowest.total) return cabinPrices[category]
      return lowest
    }, cabinPrices[Object.keys(cabinPrices)[0]])

    return lowestOverallPriceDetails.promotion ?? null
  }, [cabinPrices])

  const hasOnboardCreditInclusions = useMemo(() => {
    return [...standardInclusions, ...luxPlusInclusions]
  }, [standardInclusions, luxPlusInclusions])

  const hasPromotionData = useMemo(() => {
    return !!(
      departure.lowestOverallPriceDetails.discountPills?.discountPercentage ||
      promotion?.onBoardCredit ||
      hasOnboardCreditInclusions ||
      cruise.evergreenInclusions?.length
    )
  }, [
    departure.lowestOverallPriceDetails.discountPills?.discountPercentage,
    promotion?.onBoardCredit,
    hasOnboardCreditInclusions,
    cruise.evergreenInclusions,
  ])

  return (
    <Tile>
      <Group direction="vertical" tabletDirection="horizontal" verticalAlign="center" gap={16} tabletGap={4}>
        <Group direction="vertical" gap={20} fullWidth>
          <ContentContainer>
            <Group direction="horizontal" gap={20} horizontalAlign="stretch" verticalAlign="center">
              <ContentBox>
                <Group direction="vertical" gap={8}>
                  <Caption variant="large" weight="bold" wrap="no-wrap">
                    {portDetails?.departure}
                  </Caption>
                  <Group direction="vertical">
                    <Heading variant="heading5" wrap="no-wrap">
                      {cabinFormattedDates?.departure}
                    </Heading>
                    <BodyText variant="small" colour="neutral-one">
                      {cabinFormattedDates?.departureWeekday}
                    </BodyText>
                  </Group>
                </Group>
              </ContentBox>

              <ArrowContainer>
                <Group direction="vertical" gap={8} horizontalAlign="center">
                  <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" fullWidth>
                    <Arrow>
                      <ArrowLine className="line" />
                      <ArrowPoint className="point" />
                    </Arrow>
                  </Group>
                  <Caption variant="large" weight="bold" colour="neutral-one">
                    {pluralizeToString('Night', cruise.duration)}
                  </Caption>
                </Group>
              </ArrowContainer>

              <ContentBox>
                <Group direction="vertical" gap={4} verticalAlign="center">
                  <Caption variant="large" weight="bold" wrap="no-wrap">
                    {portDetails?.arrival}
                  </Caption>
                  <Group direction="vertical">
                    <Heading variant="heading5" wrap="no-wrap">
                      {cabinFormattedDates?.arrival}
                    </Heading>
                    <BodyText variant="small" colour="neutral-one">
                      {cabinFormattedDates?.arrivalWeekday}
                    </BodyText>
                  </Group>
                </Group>
              </ContentBox>
            </Group>
          </ContentContainer>

          <Divider kind="secondary" />

          <Group gap={8} direction="vertical">
            {hasPromotionData && <Group gap={8} direction="vertical" desktopGap={16} desktopDirection="horizontal" horizontalAlign="start">
              <CruiseEvergreenInclusionList evergreenInclusions={cruise.evergreenInclusions} />

              {departure.lowestOverallPriceDetails.discountPills?.discountPercentage && <PriceRowValueDiscount
                type="estimate"
                discountPercentage={departure.lowestOverallPriceDetails.discountPills?.discountPercentage}
              />}

              <CruiseStandardOnboardCreditLabel standardInclusions={standardInclusions} legacyPromotion={promotion} />

              {!!luxPlusInclusions?.length && <CruiseLuxPlusOnboardCreditLabel
                luxPlusInclusions={luxPlusInclusions}
                offer={cruise}
                isModalPrimaryActionHidden
              />}
            </Group>}

            {!!cabinPrices && <Prices>
              {CABIN_CATEGORIES.map((item) => {
                const cabinPrice = cabinPrices[item.category.toLowerCase()]

                const {
                  primaryPrice,
                  secondaryPrice,
                } = getCruiseOfferPriceVariation(cruise, cabinPrice)
                const hasPrice = !!(primaryPrice.total || secondaryPrice?.total)
                return (
                  <VerticalSpacer key={item.category} gap={4}>
                    {hasPrice && <PriceRowPriceCaption onInfoIconClick={() => onCabinInfoModal(item.category as App.CabinCategoryType)}>
                      {item.category} from
                    </PriceRowPriceCaption>}

                    {!!primaryPrice.total && <PriceRowPrice
                      size="S"
                      price={primaryPrice.total}
                      saleUnit={primaryPrice.type}
                    />}

                    {secondaryPrice && <CabinPrice>
                      <Caption variant="large" colour="neutral-three" as="span">
                        <FormatCurrency value={secondaryPrice.total} />
                      </Caption>
                      <Caption variant="large" colour="neutral-three" as="span">
                        /{secondaryPrice.type}
                      </Caption>
                    </CabinPrice>}
                    {!hasPrice && <Group direction="vertical" gap={8}>
                      <Caption variant="large" weight="normal" colour="neutral-two">
                        {item.category}
                      </Caption>
                      <Heading variant="heading5" colour="neutral-three">
                        Unavailable
                      </Heading>
                    </Group>}

                    {hasPrice && <Caption variant="medium" colour="neutral-two">
                      Twin Share
                    </Caption>}
                  </VerticalSpacer>
                )
              })}
            </Prices>}

            {!cabinPrices && <Group direction="horizontal" horizontalAlign="center">
              <BodyText variant="medium" weight="normal" colour="neutral-two">
                No prices available
              </BodyText>
            </Group>}
          </Group>
        </Group>

        <SeparatorHorizontal />

        <VerticalSpacer gap={12}>
          <TextButton
            kind="primary"
            fit="full-width"
            onClick={() => onHandleDepartureModal(departure)}
          >
            Select departure
          </TextButton>
        </VerticalSpacer>
      </Group>
    </Tile>
  )
}

export default CruiseDepartures
